import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Bell, BellOff, RefreshCw, Send, CheckCircle2, AlertTriangle, Loader2, Mail, Clock, Activity,
  MessageSquare, Hash, Save, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return "—"; }
}

const KindBadge = ({ kind }) => {
  const meta = {
    alert:    { icon: AlertTriangle,   label: "ALERT",    cls: "text-brand-onair border-brand-onair/40 bg-brand-onair/10" },
    recovery: { icon: CheckCircle2,    label: "RECOVERY", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10" },
    test:     { icon: Send,            label: "TEST",     cls: "text-brand-blue border-brand-blue/40 bg-brand-blue/10" },
  }[kind] || { icon: Activity, label: (kind || "").toUpperCase(), cls: "text-ink-muted border-line bg-bg-elevated" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${meta.cls}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
};

const DeliveryPills = ({ row }) => (
  <span className="inline-flex gap-1 ml-2">
    {row.email_id ? <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">email</span> : null}
    {row.slack_ok ? <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">slack</span> : null}
    {row.discord_ok ? <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">discord</span> : null}
  </span>
);

const StateBadge = ({ ok }) => {
  if (ok === null || ok === undefined) {
    return <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">— not yet checked</span>;
  }
  return ok ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Healthy
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-brand-onair">
      <span className="h-1.5 w-1.5 rounded-full bg-brand-onair animate-pulse" /> Unhealthy
    </span>
  );
};

export default function ProviderAlerts() {
  const [data, setData] = useState(null);
  const [channels, setChannels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);

  // Channel editor local state
  const [slackUrl, setSlackUrl] = useState("");
  const [discordUrl, setDiscordUrl] = useState("");
  const [showSlack, setShowSlack] = useState(false);
  const [showDiscord, setShowDiscord] = useState(false);
  const [enabledSet, setEnabledSet] = useState(new Set(["email"]));
  const [savingChannels, setSavingChannels] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/providers/alerts/status"),
        api.get("/providers/alerts/channels"),
      ]);
      setData(r.data);
      setChannels(c.data);
      setEnabledSet(new Set(c.data.enabled_channels || ["email"]));
      setSlackUrl(""); // never re-populate the secret — placeholder shows masked
      setDiscordUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load alert status");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/providers/alerts/run-now");
      toast.success(`Sweep complete — ${r.data.checked} BYOK provider${r.data.checked === 1 ? "" : "s"} checked`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally { setRunning(false); }
  };

  const testSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/providers/alerts/test-send");
      const channels = [];
      if (r.data.email_id) channels.push("email");
      if (r.data.slack_ok) channels.push("Slack");
      if (r.data.discord_ok) channels.push("Discord");
      if (channels.length) toast.success(`Test alert sent via ${channels.join(", ")}`);
      else toast.error("No channels delivered — check Resend / webhook configuration");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setSending(false); }
  };

  const toggleChannel = (ch) => {
    setEnabledSet((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch); else next.add(ch);
      if (next.size === 0) next.add("email"); // can't disable all
      return next;
    });
  };

  const saveChannels = async () => {
    setSavingChannels(true);
    try {
      const body = { enabled_channels: Array.from(enabledSet) };
      if (slackUrl) body.slack_webhook = slackUrl.trim();
      if (discordUrl) body.discord_webhook = discordUrl.trim();
      const r = await api.put("/providers/alerts/channels", body);
      setChannels(r.data);
      setSlackUrl(""); setDiscordUrl("");
      toast.success("Channel routing saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingChannels(false); }
  };

  const clearSlack = async () => {
    setSavingChannels(true);
    try {
      const r = await api.put("/providers/alerts/channels", { slack_webhook: "" });
      setChannels(r.data); toast.success("Slack webhook cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
    finally { setSavingChannels(false); }
  };
  const clearDiscord = async () => {
    setSavingChannels(true);
    try {
      const r = await api.put("/providers/alerts/channels", { discord_webhook: "" });
      setChannels(r.data); toast.success("Discord webhook cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
    finally { setSavingChannels(false); }
  };

  if (loading) return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading alert status…</div>;
  if (!data) return null;

  const items = data.items || [];
  const recent = data.recent || [];
  const resendOk = !!data.resend_configured;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-fade-up" data-testid="provider-alerts-page">
      <Link to="/dashboard/providers" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to AI Providers
      </Link>

      <PageHeader
        eyebrow="// PROVIDER ALERTS"
        title="Get pinged when your AI stack breaks."
        description="BroadcastFlow probes every connected BYOK provider every 10 minutes. The moment one flips from healthy to unhealthy, we email the station owner — with a 24-hour cooldown so you're never spammed."
      />

      {/* Resend config banner */}
      <div
        data-testid="resend-status"
        className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${
          resendOk ? "border-emerald-400/30 bg-emerald-500/5" : "border-brand-onair/40 bg-brand-onair/5"
        }`}
      >
        {resendOk ? <Bell className="h-5 w-5 text-emerald-300 mt-0.5" /> : <BellOff className="h-5 w-5 text-brand-onair mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">
            {resendOk ? "Email delivery is live (Resend configured)" : "Resend not configured — alerts will be skipped"}
          </div>
          <div className="text-xs text-ink-muted mt-1">
            {resendOk
              ? "Set RESEND_FROM_EMAIL to a verified sender domain to send to addresses other than the Resend sandbox."
              : "Set RESEND_API_KEY in /app/backend/.env to enable alert emails."}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="mb-8 grid md:grid-cols-2 gap-3">
        <button
          onClick={runNow}
          disabled={running}
          data-testid="run-now-btn"
          className="group flex items-center justify-between rounded-xl border border-line bg-bg-surface p-5 text-left lift disabled:opacity-50"
        >
          <div>
            <div className="font-display text-base font-bold flex items-center gap-2">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-brand-amber" />}
              Run health sweep now
            </div>
            <div className="text-xs text-ink-muted mt-1">Probe every connected BYOK provider immediately and update state.</div>
          </div>
          <span className="text-brand-amber font-mono text-[10px] uppercase tracking-wider">// SWEEP</span>
        </button>

        <button
          onClick={testSend}
          disabled={sending || !resendOk}
          data-testid="test-send-btn"
          title={!resendOk ? "Configure Resend first" : ""}
          className="group flex items-center justify-between rounded-xl border border-line bg-bg-surface p-5 text-left lift disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div>
            <div className="font-display text-base font-bold flex items-center gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-brand-blue" />}
              Send test alert
            </div>
            <div className="text-xs text-ink-muted mt-1">Fires a sample alert email to the station owner — bypasses cooldown.</div>
          </div>
          <span className="text-brand-blue font-mono text-[10px] uppercase tracking-wider">// TEST</span>
        </button>
      </div>

      {/* Channel routing — Slack / Discord webhooks */}
      <div className="mb-8 rounded-xl border border-line bg-bg-surface p-5" data-testid="channels-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-bold">Channel routing</h3>
            <p className="text-xs text-ink-muted mt-0.5">Fan out alerts to email + Slack + Discord. On-call rotation friendly.</p>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">// FAN-OUT</span>
        </div>

        {/* Channel toggles */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="channel-toggles">
          {[
            { key: "email",   label: "Email",   icon: Mail,           color: "amber" },
            { key: "slack",   label: "Slack",   icon: MessageSquare,  color: "blue" },
            { key: "discord", label: "Discord", icon: Hash,           color: "purple" },
          ].map(({ key, label, icon: Icon }) => {
            const on = enabledSet.has(key);
            return (
              <button
                key={key}
                onClick={() => toggleChannel(key)}
                data-testid={`channel-toggle-${key}`}
                className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border transition ${
                  on
                    ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                    : "border-line bg-bg-elevated text-ink-muted hover:border-brand-amber/30"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
                <span className="font-mono text-[10px]">{on ? "ON" : "OFF"}</span>
              </button>
            );
          })}
        </div>

        {/* Webhook inputs */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Slack */}
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Slack incoming webhook</label>
            <div className="relative mt-1">
              <input
                type={showSlack ? "text" : "password"}
                value={slackUrl}
                onChange={(e) => setSlackUrl(e.target.value)}
                placeholder={channels?.slack_configured ? channels.slack_masked : "https://hooks.slack.com/services/T0/B0/xxxxxxxx"}
                data-testid="slack-webhook-input"
                className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 pr-9 text-xs font-mono focus:border-brand-amber focus:outline-none"
              />
              <button type="button" onClick={() => setShowSlack(!showSlack)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted">
                {showSlack ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[11px] text-ink-muted">
                {channels?.slack_configured ? <span className="text-emerald-300">✓ configured</span> : "Not configured"}
              </span>
              {channels?.slack_configured && (
                <button onClick={clearSlack} className="text-[11px] text-brand-onair hover:underline" data-testid="clear-slack">Clear</button>
              )}
            </div>
          </div>

          {/* Discord */}
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Discord webhook</label>
            <div className="relative mt-1">
              <input
                type={showDiscord ? "text" : "password"}
                value={discordUrl}
                onChange={(e) => setDiscordUrl(e.target.value)}
                placeholder={channels?.discord_configured ? channels.discord_masked : "https://discord.com/api/webhooks/000/xxxxx"}
                data-testid="discord-webhook-input"
                className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 pr-9 text-xs font-mono focus:border-brand-amber focus:outline-none"
              />
              <button type="button" onClick={() => setShowDiscord(!showDiscord)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted">
                {showDiscord ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[11px] text-ink-muted">
                {channels?.discord_configured ? <span className="text-emerald-300">✓ configured</span> : "Not configured"}
              </span>
              {channels?.discord_configured && (
                <button onClick={clearDiscord} className="text-[11px] text-brand-onair hover:underline" data-testid="clear-discord">Clear</button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end">
          <button
            onClick={saveChannels}
            disabled={savingChannels}
            data-testid="save-channels-btn"
            className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50"
          >
            {savingChannels ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save routing
          </button>
        </div>
      </div>

      {/* Per-provider state */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Per-provider state</h3>
          <button onClick={load} className="text-[11px] text-ink-muted hover:text-brand-amber inline-flex items-center gap-1" data-testid="refresh-state">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-8 text-center" data-testid="state-empty">
            <div className="text-sm text-ink-muted">No BYOK providers checked yet.</div>
            <div className="text-xs text-ink-muted mt-1">Run a sweep above, or wait for the 10-min loop.</div>
          </div>
        ) : (
          <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="state-table">
            <table className="w-full text-sm">
              <thead className="bg-bg-elevated">
                <tr className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                  <th className="text-left px-4 py-2 font-normal">Provider</th>
                  <th className="text-left px-4 py-2 font-normal">State</th>
                  <th className="text-left px-4 py-2 font-normal">Last message</th>
                  <th className="text-left px-4 py-2 font-normal">Updated</th>
                  <th className="text-left px-4 py-2 font-normal">Last alert</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.provider} className="border-t border-line" data-testid={`state-row-${it.provider}`}>
                    <td className="px-4 py-3 font-medium">{it.label}</td>
                    <td className="px-4 py-3"><StateBadge ok={it.ok} /></td>
                    <td className="px-4 py-3 text-xs text-ink-muted font-mono">
                      {it.status ? `[${it.status}] ` : ""}{it.message || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-muted">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {relativeTime(it.updated_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-muted">{it.last_alerted_at ? relativeTime(it.last_alerted_at) : "never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent log */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Recent alert log</h3>
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">// LAST 20</span>
        </div>
        {recent.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-8 text-center" data-testid="log-empty">
            <div className="text-sm text-ink-muted">No alerts logged yet.</div>
            <div className="text-xs text-ink-muted mt-1">When a provider goes down, the email will land here.</div>
          </div>
        ) : (
          <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line" data-testid="log-list">
            {recent.map((r, i) => (
              <div key={i} className="px-4 py-3 flex items-start gap-3" data-testid={`log-row-${i}`}>
                <KindBadge kind={r.kind} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.label || r.provider}
                    <DeliveryPills row={r} />
                  </div>
                  <div className="text-xs text-ink-muted mt-0.5 font-mono truncate">
                    {r.status ? `[${r.status}] ` : ""}{r.message || ""}
                  </div>
                  <div className="text-[11px] text-ink-muted mt-1 inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {r.to}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {relativeTime(r.at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
